import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, createBlock as _createBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, Teleport as _Teleport } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "error-message"
}
const _hoisted_2 = {
  key: 0,
  class: "footer"
}
const _hoisted_3 = { class: "button-wrapper" }

import { useStore } from "vuex";
import { onMounted, ref, computed, watch, nextTick } from "vue";
import type { Stipulation } from "@/models/opal";
import NextButton from "@/components/shared/NextButton.vue";
import AddressForm from "@/components/forms/address-form/AddressForm.vue";
import Modal from "@/components/shared/Modal.vue";
import type { Applicant, Address, AddressFormInputs } from "@/models/application";
import { ButtonTypes } from "@/models/modal";
import { FormModes } from "@/models/form";
import useAddressForms from "@/composables/forms/useAddressForms";
import useArrayHelpers from "@/use/arrayHelpers";
import { resolveMergeFields } from "@/use/formatter";
import CustomDialogueModal from "@/components/modals/CustomDialogueModal.vue";
import useCustomDialogueModal from "@/composables/useCustomDialogueModal";
import { AddressTypeIds } from "@/config/constants";
import useApplicationData from "@/composables/useApplicationData";

interface Props {
  stip: Stipulation;
  applicant: Applicant;
  previousAddresses: Address[];
}

const mobileBreakpoint = 576;
const tabletBreakpoint = 992;


export default /*@__PURE__*/_defineComponent({
  __name: 'AddressFormsModal',
  props: {
    stip: {},
    applicant: {},
    previousAddresses: {}
  },
  emits: ["success", "close"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const store = useStore();

const props = __props;

const { findDuplicatesWithCounts } = useArrayHelpers();
const { tollFreeNumber, hrefTFN, emailAddress } = useApplicationData();

const emit = __emit;

const addressForms = ref<InstanceType<typeof AddressForm>[] | null>();
const dialogueModalRef = ref<InstanceType<typeof Modal> | null>();

const { dialogueModalOptions, showDialogueModal, openDialogueModal, closeDialogueModal } =
  useCustomDialogueModal();

const addresses = ref<AddressFormInputs[]>([]);

const addingAddresses = ref<boolean>(false);
const savingChanges = ref<boolean>(false);

const windowWidth = computed(() => window.innerWidth);

const isMobile = computed<boolean>(() => windowWidth.value <= mobileBreakpoint);

const isTablet = computed<boolean>(() => windowWidth.value <= tabletBreakpoint);

const isDesktop = computed<boolean>(() => !isMobile.value && !isTablet.value);

const formattedAddresses = computed(() => {
  if ((addressForms.value || [])?.length > 0) {
    return addressForms.value?.map((f: any) => f.formattedAddress);
  }
  return [];
});

const { validateAddressForms, updateAddressMismatchStipAsync, errors } = useAddressForms({
  forms: addresses,
  stipulation: computed(() => props.stip),
  savingChanges,
});

function addAddress() {
  const newForm = {
    id: 0, // indicates it's a new entry
    addressId: 0,
    mode: FormModes.add,
    applicantId: props?.stip?.applicantId || props?.applicant?.id,
    address1: null,
    address2: null,
    addressTypeId: AddressTypeIds.PreviousAddress,
    addressTypeName: null,
    addressUnitTypeId: null,
    addressUnitTypeName: null,
    city: null,
    countryId: null,
    countryName: null,
    postalCode: null,
    stateId: null,
    stateName: null,
    stipulationId: props.stip.id,
  };

  addresses.value.push(newForm);
}

function resetDialogueModal() {
  dialogueModalOptions.value = {
    title: undefined,
    description: undefined,
    buttons: [],
    loading: false,
    closeBtn: false,
  };

  showDialogueModal.value = false;
}

function deleteAddress(address: AddressFormInputs, index: number) {
  if (address.id > 0) {
    return;
  }

  let description = "Are you sure you want to delete this address record?";

  if (addresses.value.length === 1) {
    description =
      "Are you sure you want to delete this address record? You must provide at least one address record.";
  }

  openDialogueModal({
    closeBtn: false,
    description: description,
    buttons: [
      {
        label: "Yes",
        type: Number(ButtonTypes.Confirm),
        onClick: async () => {
          addresses.value.splice(index, 1);

          await nextTick(() => {
            closeDialogueModal();
          });
        },
      },
      {
        label: "No",
        type: Number(ButtonTypes.Cancel),
        onClick: () => {
          closeDialogueModal();
        },
      },
    ],
  });
}

function getFormRef(address: AddressFormInputs, index: number) {
  return addressForms.value?.find(
    a => a?.address?.addressId === address?.addressId && a.props.index === index,
  );
}

function handleMatches(matches: any[]) {
  let html = "";

  if (matches && matches.length > 0) {
    matches.forEach((s: Record<number, any[]>) => {
      html += `<li style="font-size: 12px; font-weight: 400;"><span class="xhighlight">${s[0]} (${s[1]}) <span></li>`;
    });
  }

  const addresses = `<ul style="margin: 0; padding: 0; list-style: none;">${html}</ul>`;

  openDialogueModal({
    description: `<span style="color: red;">Duplicate record(s) detected</span>. <p>${addresses}</p>`,
    buttons: [
      {
        label: "Go Back and Edit",
        type: Number(ButtonTypes.Cancel),
        onClick: () => {
          showDialogueModal.value = false;
          savingChanges.value = false;
        },
      },
      {
        label: "Save Changes",
        type: Number(ButtonTypes.Confirm),
        onClick: async () => {
          savingChanges.value = true;
          await confirmAddresses();
        },
      },
    ],
    closeBtn: false,
  });
}

function handleMinimumAddressesNotMet() {
  if (addresses.value?.length === 0) {
    openDialogueModal({
      description: `You must add at least 1 address before submitting.`,
      buttons: [
        {
          label: "Ok",
          type: Number(ButtonTypes.Confirm),
          onClick: async () => {
            showDialogueModal.value = false;
            savingChanges.value = false;
          },
        },
      ],
    });

    return;
  }
}

async function confirmAddresses() {
  // close dialogue modal if it is open
  closeDialogueModal();

  let html = "";

  if (formattedAddresses.value && formattedAddresses.value.length > 0) {
    formattedAddresses.value.forEach((s: string, index: number) => {
      html += `<li style="font-size: 12px; font-weight: 400;"><span class="xhighlight">Address ${
        index + 1
      }:&nbsp;${s}<span></li>`;
    });
  }

  const addresses = `<ul style="margin: 0; padding: 0; list-style: none;">${html}</ul>`;

  openDialogueModal({
    description: `Please confirm your entries <p>${addresses}</p> Once you click "Submit", the changes will be submitted for review and you will no longer be able to modify these records.`,
    buttons: [
      {
        label: "Cancel",
        type: Number(ButtonTypes.Cancel),
        onClick: async () => {
          closeDialogueModal();
          savingChanges.value = false;
        },
      },
      {
        label: "Submit",
        type: Number(ButtonTypes.Confirm),
        onClick: async () => {
          await saveChanges();
        },
      },
    ],
  });
}

async function saveChanges() {
  dialogueModalOptions.value.loading = true;

  const response = await updateAddressMismatchStipAsync(); // need this before pushing changes

  const updatedStip = {
    ...props.stip,
    stipulationStatusId: response.results?.stipulationStatusId || props.stip.stipulationStatusId,
    stipulationStatusName:
      response.results?.stipulationStatusName || props.stip.stipulationStatusName,
  };

  if (response.success) {
    handleSuccess(updatedStip);
  } else {
    openErrorModal();
  }
}
async function handleValidationErrors(indexKeys: string[]) {
  if (indexKeys.length === 0) {
    return;
  }
  let html = "";

  indexKeys.forEach((s: string) => {
    html += `<li style="font-size: 12px; font-weight: 400;"><span class="xhighlight">${s}<span></li>`;
  });

  const addresses = `<ul style="margin: 0; padding: 0; list-style: none;">${html}</ul>`;

  openDialogueModal({
    description: `Please resolve the errors in the following entries <p style="color: red;">${addresses}</p>`,
    buttons: [
      {
        label: "Ok",
        type: Number(ButtonTypes.Cancel),
        onClick: async () => {
          closeDialogueModal();
          savingChanges.value = false;
        },
      },
    ],
  });
}

function openErrorModal() {
  dialogueModalOptions.value.loading = false;

  let error =
    "Your changes could not be saved. Please try again. If the issue persists, please contact our support team at {{phoneNumber}} or {{email}}";

  error = resolveMergeFields(error, {
    phoneNumber: `<a class="underline" style="text-decoration: underline;" :href="\`tel:${hrefTFN.value}\`">${tollFreeNumber.value}</a>`,
    email: `<a class="underline" style="text-decoration: underline;"  :href="\`mailto:${emailAddress}\`">${emailAddress.value}</a>`,
  });

  closeDialogueModal();

  openDialogueModal({
    description: error,
    closeBtn: true,
    buttons: [
      {
        label: "Ok",
        type: Number(ButtonTypes.Confirm),
        onClick: async () => {
          closeDialogueModal();
          savingChanges.value = false;
        },
      },
    ],
  });
}

function handleSuccess(response: Stipulation) {
  emit("success", response);
  dialogueModalOptions.value.loading = false;
  closeDialogueModal();
}

function handleUnsavedChanges(forms: AddressFormInputs[]) {
  if (forms.length === 0) {
    return;
  }
  let html = "";

  forms.forEach((s: AddressFormInputs, index: number) => {
    if (s.mode !== FormModes.view) {
      html += `<li style="font-size: 12px; font-weight: 400;"><span class="xhighlight">Address ${index + 1}<span></li>`;
    }
  });

  const addresses = `<ul style="margin: 0; padding: 0; list-style: none;">${html}</ul>`;

  openDialogueModal({
    description: `You have made changes that haven't been saved yet or open forms. Please review the following entries and click the "Save Address" button. <p>${addresses}</p>`,
    buttons: [
      {
        label: "Ok",
        type: Number(ButtonTypes.Cancel),
        onClick: async () => {
          closeDialogueModal();
          savingChanges.value = false;
        },
      },
    ],
  });
}
async function handleSubmit() {
  savingChanges.value = true;
  errors.value = [];

  try {
    if (addresses.value?.length === 0) {
      handleMinimumAddressesNotMet();
    }

    // check if any unsaved changes
    if (!addresses.value.every(a => a.mode === FormModes.view)) {
      handleUnsavedChanges(addresses.value);
      return;
    }
    addressForms.value?.forEach((element: InstanceType<typeof AddressForm>) => {
      element?.validate();
    });

    const validationResponse = await validateAddressForms();

    if (validationResponse === null) {
      // Not a validation error, something else went wrong
      openErrorModal();
      return;
    }

    if ((validationResponse.indexKeys || [])?.length > 0) {
      handleValidationErrors(validationResponse.indexKeys || []);
      return;
    }

    if (validationResponse.valid) {
      if (addresses.value.length >= 1) {
        const duplicates = findDuplicatesWithCounts(formattedAddresses.value || []);

        if (duplicates && duplicates.length > 0) {
          handleMatches(duplicates as any[]);
        } else {
          confirmAddresses();
        }
      }
    } else {
      savingChanges.value = false;
    }
  } catch {
    savingChanges.value = false;
  }
}

async function closeAddressModal() {
  openDialogueModal({
    closeBtn: false,
    description: `Address information will not be reviewed until you click "submit" button below. Are you sure you want to close this window without submitting?`,
    buttons: [
      {
        label: "Yes",
        type: Number(ButtonTypes.Confirm),
        onClick: async () => {
          addresses.value = addresses.value.filter((a: AddressFormInputs) => a.id > 0);

          emit("close");
          showDialogueModal.value = false;
          savingChanges.value = false;
        },
      },
      {
        label: "No",
        type: Number(ButtonTypes.Cancel),
        onClick: async () => {
          showDialogueModal.value = false;
          savingChanges.value = false;
        },
      },
    ],
  });
}

function initializeDialogueModal() {
  try {
    if (
      dialogueModalRef.value &&
      dialogueModalRef.value.modalHeader &&
      dialogueModalRef.value.modalBody &&
      dialogueModalRef.value.modalContainer
    ) {
      dialogueModalRef.value.modalHeader.style.height = "0";
      dialogueModalRef.value.modalContainer.style.maxWidth = "50%";
      dialogueModalRef.value.modalBody.style.minHeight = "100px";
      dialogueModalRef.value.modalContainer.classList.add("xs-mobile-w100 xs-text-center");
    }
  } catch {
    return;
  }
}

watch(
  () => dialogueModalRef.value,
  value => {
    if (value) {
      initializeDialogueModal();
    } else {
      resetDialogueModal();
    }
  },
);

onMounted(() => {
  props.previousAddresses.forEach((a: any) => {
    addresses.value.push({
      ...a,
      addressId: a.id,
      mode: FormModes.view,
      stipulationId: props?.stip?.id,
    });
  });

  if (addresses.value.length === 0) {
    addAddress();
  }
});

__expose({
  addresses,
  getFormRef,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Modal, {
      class: "address-forms-modal",
      ref: "AddressFormsModalRef",
      onClose: closeAddressModal
    }, {
      footer: _withCtx(() => [
        (addresses.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", { class: "link-wrapper desktop xs-mobile" }, [
                _createElementVNode("a", {
                  class: "underline",
                  onClick: addAddress
                }, "+ Add Another Address")
              ]),
              _createElementVNode("div", _hoisted_3, [
                _createVNode(NextButton, {
                  ref: "submitButton",
                  onClick: handleSubmit,
                  disabled: addingAddresses.value || addresses.value.length === 0,
                  loading: savingChanges.value,
                  text: "Submit",
                  class: "default-btn submit-btn"
                }, null, 8, ["disabled", "loading"])
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", {
          class: _normalizeClass(["address-forms", { hidden: addresses.value?.length === 0 }])
        }, [
          (addresses.value?.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, " Please add at least one address to proceed. Click the + Add Address below "))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(addresses.value, (address, index) => {
            return (_openBlock(), _createBlock(AddressForm, {
              key: `stip_${__props.stip.id}_${index}`,
              ref_for: true,
              ref_key: "addressForms",
              ref: addressForms,
              applicant: __props.applicant,
              stipulation: __props.stip,
              addresses: addresses.value,
              previousAddresses: __props.previousAddresses,
              address: address,
              index: index,
              disabled: savingChanges.value,
              loading: savingChanges.value,
              onOnDelete: ($event: any) => (deleteAddress($event.address, $event.index)),
              onOnError: openErrorModal,
              onCloseDialogueModal: _unref(closeDialogueModal)
            }, null, 8, ["applicant", "stipulation", "addresses", "previousAddresses", "address", "index", "disabled", "loading", "onOnDelete", "onCloseDialogueModal"]))
          }), 128)),
          (addresses.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: _normalizeClass(["link-wrapper", [{ isVisible: addresses.value.length === 0 }]])
              }, [
                _createElementVNode("a", {
                  class: "underline",
                  onClick: addAddress
                }, "+ Add Address")
              ], 2))
            : _createCommentVNode("", true)
        ], 2)
      ]),
      _: 1
    }, 512),
    (_openBlock(), _createBlock(_Teleport, { to: "#modal-target" }, [
      (_unref(showDialogueModal))
        ? (_openBlock(), _createBlock(CustomDialogueModal, {
            key: 0,
            ref_key: "dialogueModalRef",
            ref: dialogueModalRef,
            loading: _unref(dialogueModalOptions).loading,
            options: _unref(dialogueModalOptions)
          }, null, 8, ["loading", "options"]))
        : _createCommentVNode("", true)
    ]))
  ], 64))
}
}

})